import { format } from 'date-fns'
import { OnlyViewHoc } from 'hocs'
import React, { useState } from 'react'

import { getFio } from 'helpers/formatText'
import { useStores } from 'hooks/useStores'
import { acquiredClientsApi } from 'redux/services/acquired-clients'
import { GetAcquiredClientsParams } from 'redux/services/acquired-clients/types'
import { useGetSupervisorsQuery } from 'redux/services/common'

import { AppButton } from 'components/AppButton'
import AppDatePicker from 'components/AppDatePicker/AppDatePicker'
import { AppFiltersLayout } from 'components/AppFiltersLayout'
import { AppSelect } from 'components/AppSelect'
import { TextField } from 'components/TextField'
import { KG_PHONE_MAX_LENGTH, TextFieldPhone } from 'components/TextFieldPhone'

interface Props {
  onSubmit?(values: GetAcquiredClientsParams): void
}

const CURRENT_DATE = new Date()
const CURRENT_MONTH_RANGE = {
  start: format(
    new Date(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 1),
    'yyyy-MM-dd',
  ),
  end: format(CURRENT_DATE, 'yyyy-MM-dd'),
}

export const INITIAL_PARAMS: GetAcquiredClientsParams = {
  search: '',
  acquisition_start: CURRENT_MONTH_RANGE.start,
  acquisition_end: CURRENT_MONTH_RANGE.end,
  employee: '',
  supervisor: '',
  turnover_start: CURRENT_MONTH_RANGE.start,
  turnover_end: CURRENT_MONTH_RANGE.end,
}

const defaultListValue = {
  title: 'Все',
  value: '',
}

export const Filters: React.FC<Props> = ({ onSubmit }) => {
  const [isDisabled, setIsDisabled] = useState(false)
  const { dialogStore } = useStores()
  const [params, setParams] = React.useState<GetAcquiredClientsParams>(
    INITIAL_PARAMS,
  )
  const { data: supervisors } = useGetSupervisorsQuery()
  const [generateExcel, generateExcelState] =
    acquiredClientsApi.useGenerateAcquiredClientsExcelMutation()

  const employee = '+' + params?.employee?.replace(/\D/g, '')
  const supervisor = '+' + params?.supervisor?.replace(/\D/g, '')

  function _submit() {
    const _params = {
      ...params,
      employee: employee.length === KG_PHONE_MAX_LENGTH ? employee : '',
      supervisor: supervisor.length === KG_PHONE_MAX_LENGTH ? supervisor : '',
    }
    onSubmit?.(_params)
  }

  function _reset() {
    setParams(INITIAL_PARAMS)
    onSubmit?.(INITIAL_PARAMS)
  }

  function _handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target
    setParams(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  function setParamByName(
    name: keyof GetAcquiredClientsParams,
    value: string | number | boolean,
  ) {
    setParams({
      ...params,
      [name]: value,
    })
  }

  const handleClickGenerateReport = async () => {
    try {
      setIsDisabled(true)
      await generateExcel(params)
      dialogStore.openModal({
        title: 'Успех',
        description:
          'Ваш файл генерируется. Для того, чтобы отследить процесс или скачать файл, перейдите на вкладку "Ручные отчеты / Привлеченные клиенты"',
      })
    } catch {
    } finally {
      setTimeout(() => setIsDisabled(false), 10000)
    }
  }

  const selectedSupervisorText = getFio(
    supervisors?.results?.find(s => s.phone === params.supervisor),
  )

  return (
    <AppFiltersLayout
      controls={
        <>
          <AppButton size="small" onClick={_submit}>
            Применить
          </AppButton>
          <AppButton size="small" variant="red" onClick={_reset}>
            Сбросить
          </AppButton>
          <OnlyViewHoc>
            <AppButton
              onClick={handleClickGenerateReport}
              disabled={generateExcelState.isLoading || isDisabled}
              variant="siren_dashed_border"
              size="small"
            >
              Сгенерировать отчет
            </AppButton>
          </OnlyViewHoc>
        </>
      }
    >
      <TextField
        inputSize="small"
        onChange={_handleChange}
        value={params.search}
        name="search"
        label="Название"
        placeholder="Введите название"
      />
      <TextFieldPhone
        required={false}
        inputSize="small"
        onChange={_handleChange}
        onChangePhone={value => setParamByName('employee', value.phone)}
        value={params.employee || ''}
        name="employee"
        label="Консультант"
        placeholder="Введите консультанта"
      />
      <TextFieldPhone
        inputSize="small"
        required={false}
        onChange={_handleChange}
        onChangePhone={value => setParamByName('supervisor', value.phone)}
        value={params.supervisor || ''}
        name="supervisor"
        label="Супервайзер"
        placeholder="Введите супервайзера"
      />

      <AppSelect
        size="extra_small"
        label="Выберите супервайзера"
        text={selectedSupervisorText}
        onChangeValues={([selectedValue]) => {
          setParamByName('supervisor', selectedValue)
        }}
        list={[
          defaultListValue,
          ...(supervisors?.results || []).map(item => ({
            value: item.phone,
            title: getFio(item),
          })),
        ]}
      />

      <AppDatePicker
        label="Начало периода"
        inputSize="small"
        value={params.acquisition_start || ''}
        onchange={date => {
          setParamByName('acquisition_start', date)
        }}
      />

      <AppDatePicker
        label="Конец периода"
        inputSize="small"
        value={params.acquisition_end || ''}
        onchange={date => {
          setParamByName('acquisition_end', date)
        }}
      />

      <AppDatePicker
        label="Начало периода оборотов"
        inputSize="small"
        value={params.turnover_start || ''}
        onchange={date => {
          setParamByName('turnover_start', date)
        }}
      />

      <AppDatePicker
        label="Конец периода оборотов"
        inputSize="small"
        value={params.turnover_end || ''}
        onchange={date => {
          setParamByName('turnover_end', date)
        }}
      />
    </AppFiltersLayout>
  )
}
